<template>
  <div class="main-body">
    <div class="title-bar">
      <h1>Sales Impact Report</h1>
      <div class="actions">
        <div class="icon-button" @click="showCharts = !showCharts">
          <img id="noHover" src="@/assets/chart.svg" />
          <img id="hover" src="@/assets/chart-hover.svg" />
        </div>
        <div class="icon-button" @click="printReport">
          <img id="noHover" src="@/assets/print.svg" />
          <img id="hover" src="@/assets/print-hover.svg" />
        </div>
        <div class="icon-button" @click="downloadReport">
          <img id="noHover" src="@/assets/download.svg" />
          <img id="hover" src="@/assets/download-hover.svg" />
        </div>
        <div class="icon-button" @click="showFilter = true">
          <img id="noHover" src="@/assets/filter.svg" />
          <img id="hover" src="@/assets/filter-hover.svg" />
        </div>

        <button :class="{ 'square-btn': true, saving: loading }" :disabled="loading" @click="handleRunReport">Run Report<img src="@/assets/spinner.svg" /></button>
      </div>
    </div>

    <div class="print-title-bar">
      <h1>Sales Impact Report</h1>
    </div>

    <div class="scrollable-div display-scrollbar" ref="scrollableDiv" @scroll="handleScroll">
      <SalesImpactReportCharts v-if="reportData.length > 0 && showCharts" />
      <table class="full-width-table sortable-table resizeable-table ellipsis-table">
        <tbody>
          <tr class="table-header">
            <th
              v-for="column in sortedColumns"
              :key="column.key"
              :style="{ width: `${column.width || column.flexWidth * 5}%` }"
              @click="handleSort(column.key)"
              :class="{ 'force-show-resize-handle': currentColumn && currentColumn.key === column.key && resizing, 'force-dont-show-resize-handle': currentColumn && currentColumn.key != column.key && resizing }">
              {{ column.title }}
              <img :class="{ 'sort-arrow': true, desc: sortDirection === 'desc' && column.key === sortColumn }" src="@/assets/sort-arrow.svg" />
              <div class="resize-handle" @mousedown.stop="startResize($event, column)" @click.stop=""><span></span></div>
            </th>
          </tr>
          <tr v-if="!loading" v-for="customer in sortedReportData" :key="customer.LEADID">
            <td v-for="column in sortedColumns" :key="column.key">
              <span>{{ customer[column.key] || '' }}</span>
            </td>
          </tr>
          <tr v-if="loading" class="loading-row">
            <td :colspan="sortedColumns.length"><img src="@/assets/spinner.svg" /></td>
          </tr>
          <tr v-if="sortedReportData.length === 0 && !loading" class="no-results">
            <td :colspan="sortedColumns.length">No data found</td>
          </tr>
        </tbody>
      </table>
    </div>

    <SalesImpactReportFilter v-if="showFilter" @closePopup="showFilter = false" />
  </div>
</template>

<script>
import SalesImpactReportFilter from '@/components/SalesImpactReportFilter.vue';
import { useSalesImpactReportQuery, useSalesImpactReportFilters } from '@/composables/useSalesImpactReportQuery';
import SalesImpactReportCharts from '@/components/SalesImpactReportCharts.vue';

export default {
  name: 'SalesImpactReport',
  components: {
    SalesImpactReportFilter,
    SalesImpactReportCharts,
  },
  setup() {
    const { filters, columnsList, runReport, loading, resetFilters, reportData } = useSalesImpactReportQuery();
    const { runGetFilters } = useSalesImpactReportFilters();

    runGetFilters();

    return {
      filters,
      columnsList,
      runReport,
      loading,
      resetFilters,
      reportData,
    };
  },
  data() {
    return {
      showFilter: false,
      sortColumn: 'CREATED',
      sortDirection: 'asc',
      resizing: false,
      currentColumn: null,
      startX: 0,
      startWidth: 0,
      maxRows: 100,
      rowIncrement: 50,
      showCharts: false,
    };
  },
  computed: {
    sortedColumns() {
      return this.filters.columns
        .filter((colName) => this.columnsList.find((col) => col.name === colName))
        .map((colName) => {
          const column = this.columnsList.find((col) => col.name === colName);
          return {
            key: column.name,
            title: column.title,
            flexWidth: column.flexWidth,
            order: column.order,
          };
        })
        .sort((a, b) => a.order - b.order);
    },
    sortedReportData() {
      if (!this.reportData) return [];
      const sortedData = [...this.reportData].sort((a, b) => {
        let valueA = a[this.sortColumn];
        let valueB = b[this.sortColumn];

        // If sorting by CREATED or COMPLETED, treat as date
        if (this.sortColumn === 'CREATED' || this.sortColumn === 'COMPLETED') {
          // Try to parse as date, fallback to 0 if invalid or empty
          const parseDate = (val) => {
            if (!val) return 0;
            // Try to parse MM/DD/YYYY
            const parts = val.split('/');
            if (parts.length === 3) {
              // new Date(year, monthIndex, day)
              const d = new Date(+parts[2], +parts[0] - 1, +parts[1]);
              return d.getTime() || 0;
            }
            // fallback: try Date.parse
            const d = new Date(val);
            return d.getTime() || 0;
          };
          const dateA = parseDate(valueA);
          const dateB = parseDate(valueB);

          if (!dateA && !dateB) return 0;
          if (!dateA) return 1;
          if (!dateB) return -1;

          if (this.sortDirection === 'asc') {
            return dateA - dateB;
          } else {
            return dateB - dateA;
          }
        } else {
          // Default string sort
          valueA = (valueA ?? '').toString();
          valueB = (valueB ?? '').toString();
          if (!valueA && !valueB) return 0;
          if (!valueA) return 1;
          if (!valueB) return -1;
          if (this.sortDirection === 'asc') {
            return valueA.localeCompare(valueB);
          } else {
            return valueB.localeCompare(valueA);
          }
        }
      });

      return sortedData.slice(0, this.maxRows);
    },
  },
  methods: {
    handleRunReport() {
      this.maxRows = 100; // Reset to initial value when running a new report
      this.runReport();
    },
    handleScroll(event) {
      const element = event.target;
      const scrollTop = element.scrollTop;
      const scrollHeight = element.scrollHeight;
      const clientHeight = element.clientHeight;

      // Check if we're near the bottom (within 100px)
      const nearBottom = scrollTop + clientHeight >= scrollHeight - 100;

      if (nearBottom && this.maxRows < this.reportData.length && !this.loading) {
        // Load more rows if we haven't displayed all data yet
        this.maxRows += this.rowIncrement;
      }
    },
    handleSort(column) {
      if (this.resizing) return;
      if (this.sortColumn === column) {
        // Toggle sort direction if clicking the same column
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        // Set new sort column and default to ascending
        this.sortColumn = column;
        this.sortDirection = 'asc';
      }
    },
    startResize(event, column) {
      event.preventDefault();
      this.resizing = true;
      this.currentColumn = column;

      // Find the th element by traversing up the DOM tree
      let columnElement = event.target.parentElement;
      while (columnElement && columnElement.tagName !== 'TH') {
        columnElement = columnElement.parentElement;
      }

      if (!columnElement) {
        console.error('Could not find th element');
        return;
      }

      this.startX = event.pageX;
      this.startWidth = columnElement.offsetWidth;

      document.addEventListener('mousemove', this.handleResize);
      document.addEventListener('mouseup', this.stopResize);
    },
    handleResize(event) {
      if (!this.resizing) return;

      const deltaX = event.pageX - this.startX;
      const newWidth = this.startWidth + deltaX;
      const tableWidth = this.$el.querySelector('table').offsetWidth;

      // Calculate what percentage 5rem is of the table width
      const minWidthInPixels = 5 * 16; // 5rem = 5 * 16px (assuming 1rem = 16px)
      const minWidthPercentage = (minWidthInPixels / tableWidth) * 100;

      // Ensure the new width is not less than 5rem
      const percentageWidth = Math.max((newWidth / tableWidth) * 100, minWidthPercentage);

      // Update the column width
      this.currentColumn.width = percentageWidth;

      // Force Vue to update the view
      this.$forceUpdate();
    },
    stopResize() {
      setTimeout(() => {
        this.resizing = false;
      }, 10);
      this.currentColumn = null;
      document.removeEventListener('mousemove', this.handleResize);
      document.removeEventListener('mouseup', this.stopResize);
    },
    printReport() {
      window.print();
    },
    downloadReport() {
      if (!this.reportData || this.reportData.length === 0) {
        console.warn('No data to download');
        return;
      }

      // Define columns for export (all columns except IDs, with individual city/state/zip instead of location)
      const exportColumns = [
        { key: 'CREATED', title: 'Created' },
        { key: 'NAME', title: 'Name' },
        { key: 'SOURCE', title: 'Source' },
        { key: 'CHANNEL', title: 'Channel' },
        { key: 'CITY', title: 'City' },
        { key: 'STATE', title: 'State' },
        { key: 'POSTALCODE', title: 'Postal Code' },
        { key: 'INTERACTIONS', title: 'Interactions' },
        { key: 'STATUS', title: 'Status' },
        { key: 'ORDERVALUE', title: 'Order Value' },
        { key: 'COMPLETED', title: 'Completed' },
      ];

      // Create CSV headers from column titles
      const headers = exportColumns.map((col) => col.title);

      // Create CSV rows from report data
      const csvRows = [
        headers.join(','), // Header row
        ...this.reportData.map((row) => {
          return exportColumns
            .map((col) => {
              const value = row[col.key] || '';
              // Escape quotes and wrap in quotes if value contains comma, quote, or newline
              const stringValue = String(value);
              if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
                return `"${stringValue.replace(/"/g, '""')}"`;
              }
              return stringValue;
            })
            .join(',');
        }),
      ];

      // Create CSV content
      const csvContent = csvRows.join('\n');

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');

      if (link.download !== undefined) {
        // Generate filename with current date
        const now = new Date();
        const dateStr = now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0') + '-' + String(now.getDate()).padStart(2, '0');
        const filename = `sales-impact-report-${dateStr}.csv`;

        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    },
  },
};
</script>

<style scoped lang="scss">
.print-title-bar {
  display: none;
}

@media print {
  @page {
    size: landscape;
    padding: 10px;
  }

  .title-bar {
    display: none;
  }

  .print-title-bar {
    display: block;
    text-align: center;
    margin-bottom: 20px;
    padding: 10px;
    border-bottom: 2px solid #000;

    h1 {
      font-size: 24px;
      margin: 0 0 10px 0;
    }

    .print-details {
      font-size: 12px;
      color: #666;
      margin: 0;

      p {
        margin: 2px 0;
      }
    }
  }

  .scrollable-div {
    height: auto !important;
    page-break-inside: auto;
  }

  table {
    width: 100% !important;
    border-collapse: collapse;
    page-break-inside: auto;
  }

  tr {
    page-break-inside: avoid;
    page-break-after: auto;
  }

  th,
  td {
    border: 1px solid #ddd;
    padding: 8px;
    text-align: left;
    font-size: 12px !important;
    padding-left: 5px !important;
    padding-right: 5px !important;
    padding-top: 2px !important;
    padding-bottom: 2px !important;
  }

  th {
    background-color: #f5f5f5 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  tr:nth-child(even) {
    background-color: #f9f9f9 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .link {
    text-decoration: none;
    color: black;
  }

  .sort-arrow,
  .resize-handle {
    display: none;
  }
}
</style>
